import { DbEnum } from "@nexus/types/core"
import { createSlice } from "@reduxjs/toolkit"

import type { OptimisticMessage } from "@/common/types/chat"
import { getMimeTypeFromFilename } from "@/shared/lib/file-upload/utils/attachment.utils"

import * as reducers from "./reducers"
import { mergeMessagesWithOptimistic } from "./reducers/messages"
import { createInitialState } from "./state"
import { loadMessages } from "./thunks/loadMessages"
import { loadMorePlaygroundMessages } from "./thunks/loadMoreMessages"
import { loadPlaygroundChats } from "./thunks/loadPlaygroundChats"
import { sendMessage } from "./thunks/sendMessage"
import { stopPlaygroundMessage } from "./thunks/stopMessage"

/**
 * Playground chat slice
 *
 * NOTE: TypeScript shows "excessively deep" type inference error
 * This is a known Redux Toolkit limitation with union types (ReduxMessage = Message | OptimisticMessage)
 * The slice works correctly at runtime - suppressing type error
 */
export const playgroundChatSlice = createSlice({
  name: "playgroundChat",
  initialState: createInitialState(),
  reducers: {
    openChat: reducers.openChat,
    closeChat: reducers.closeChat,
    setActiveChat: reducers.setActiveChat,
    setPlaygroundSelectedAgent: reducers.setPlaygroundSelectedAgent,
    updateChatSettings: reducers.updateChatSettings,
    addMessage: reducers.addMessage,
    setMessages: reducers.setMessages,
    addMessageFromSocket: reducers.addMessageFromSocket,
    updateChatTopic: reducers.updateChatTopic,
    addStreamingToken: reducers.addStreamingToken,
    completeStreamingMessage: reducers.completeStreamingMessage,
    clearStreamingMessage: reducers.clearStreamingMessage,
    clearChatStreamingMessages: reducers.clearChatStreamingMessages,
    streamingError: reducers.streamingError,
    stopSending: reducers.stopSending,
    clearNewlyCreatedChatId: reducers.clearNewlyCreatedChatId,
    setNewlyCreatedChatId: reducers.setNewlyCreatedChatId,
    setLoadingChatHistory: reducers.setLoadingChatHistory,
    setHasCodeInterpreterSession: reducers.setHasCodeInterpreterSession,
    setSandboxJustReset: reducers.setSandboxJustReset,
    // Code interpreter streaming (unified into streamingMessages.blocks)
    addStreamingBlock: reducers.addStreamingBlock,
    updateStreamingBlock: reducers.updateStreamingBlock,
    completeStreamingBlock: reducers.completeStreamingBlock,
    completeCodeInterpreterStreaming: reducers.completeCodeInterpreterStreaming
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMessage.pending, (state, action) => {
        const chatId = action.meta.arg.chatId || action.meta.arg.context?.chatRoomId
        if (!chatId) return

        const chat = state.chats[chatId]
        if (chat) {
          chat.isSendingMessage = true
          // Clear reset flag — new sandbox will be created on this message
          chat.sandboxJustReset = false

          // Clear previous streaming state when starting a new message
          chat.streamingMessages = {}

          // Lock in the agent at send time - this is the moment of truth
          if (action.meta.arg.context?.agentId) {
            chat.currentAgentId = action.meta.arg.context.agentId
          }

          // Add optimistic user message immediately
          const now = new Date()

          // Build files array for optimistic display
          // Format should match backend response (see Knowledge entity)
          const optimisticFiles = (action.meta.arg.files || []).map((f) => {
            return {
              id: f.id,
              name: f.name,
              textContent: null,
              // urls array - primary source for chat attachments
              urls: f.url ? [f.url] : [],
              urlAWS: {},
              size: f.size ? String(f.size) : "0", // Backend returns string
              type: f.name.includes(".")
                ? f.name.split(".").pop()?.toUpperCase() || "UNKNOWN"
                : "UNKNOWN",
              status: "DONE",
              category: "Document",
              from: "CHAT",
              isFolder: false,
              metadata: {
                // Prefer provided mimeType (from File.type), fall back to extension-based inference
                mimeType: f.mimeType || getMimeTypeFromFilename(f.name)
              }
            }
          })

          // Optimistic message - minimal fields for UI, replaced by backend response
          const userMessage: OptimisticMessage = {
            id: action.meta.arg.messageId,
            chatId,
            content: {
              text: action.meta.arg.content,
              ...(action.meta.arg.images &&
                action.meta.arg.images.length > 0 && {
                  images: action.meta.arg.images
                })
            },
            type: DbEnum.MessageContentType.HUMAN,
            status: DbEnum.MessageStatus.IN_PROGRESS,
            createdAt: now,
            updatedAt: now,
            files: optimisticFiles,
            textContent: action.meta.arg.content
          }

          chat.messages.push(userMessage)
        }
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const { chatId } = action.payload
        const chat = state.chats[chatId]

        if (chat) {
          // NOTE: Do NOT set isSendingMessage = false here!
          // The typing indicator should stay visible until:
          // - Regular streaming: first streaming token arrives (addStreamingToken reducer)
          // - Code interpreter: first block arrives (addCodeInterpreterBlock reducer)
          // This ensures smooth UX during the delay between sending and first response

          // Backend confirmed chat exists - set isNew to false
          const wasNew = chat.isNew
          chat.isNew = false

          // Add to chat list if it was a new chat
          if (wasNew && !state.metadata.chatIds.includes(chatId)) {
            state.metadata.chatIds.unshift(chatId)
          }

          // For new chats, set newlyCreatedChatId AFTER backend confirms creation
          // This triggers URL update only when we're sure the chat exists
          if (wasNew) {
            state.metadata.newlyCreatedChatId = chatId
          }
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        const chatId = action.meta.arg.chatId || action.meta.arg.context?.chatRoomId
        const chat = chatId ? state.chats[chatId] : undefined
        if (chat) {
          chat.isSendingMessage = false
          const optimisticId = action.meta.arg.messageId
          chat.messages = chat.messages.filter((m) => m.id !== optimisticId)
        }
      })

      .addCase(loadMessages.pending, (state, action) => {
        const { chatId } = action.meta.arg
        const chat = state.chats[chatId]
        if (chat) {
          chat.isLoadingMessages = true
        }
      })
      .addCase(loadMessages.fulfilled, (state, action) => {
        const { chatId, messages, hasMore } = action.payload
        const chat = state.chats[chatId]
        if (chat) {
          chat.isLoadingMessages = false
          chat.hasMoreHistory = hasMore

          chat.messages = mergeMessagesWithOptimistic(chat.messages, messages)
        }
      })
      .addCase(loadMessages.rejected, (state, action) => {
        const { chatId } = action.meta.arg
        const chat = state.chats[chatId]
        if (chat) {
          chat.isLoadingMessages = false
        }
      })

      .addCase(loadPlaygroundChats.pending, reducers.handleLoadChatsPending)
      .addCase(loadPlaygroundChats.fulfilled, reducers.handleLoadChatsFulfilled)
      .addCase(loadPlaygroundChats.rejected, reducers.handleLoadChatsRejected)

      // stopPlaygroundMessage
      .addCase(stopPlaygroundMessage.pending, (state, action) => {
        const { chatId } = action.meta.arg
        const chat = state.chats[chatId]

        if (chat) {
          // Immediate UI feedback - stop showing as sending
          chat.isSendingMessage = false
          chat.shouldShowDynamicWhitespace = false
          chat.streamingMessages = {}
        }
      })
      .addCase(stopPlaygroundMessage.fulfilled, (state, action) => {
        const { chatId } = action.payload
        const chat = state.chats[chatId]

        if (chat) {
          chat.isSendingMessage = false
          chat.shouldShowDynamicWhitespace = false
        }
      })
      .addCase(stopPlaygroundMessage.rejected, (state, action) => {
        const { chatId } = action.meta.arg
        const chat = state.chats[chatId]

        if (chat) {
          // Even on error, stop the UI state
          chat.isSendingMessage = false
          chat.shouldShowDynamicWhitespace = false
        }
      })

      // loadMorePlaygroundMessages - infinite scroll pagination
      .addCase(loadMorePlaygroundMessages.pending, (state, action) => {
        const { chatId } = action.meta.arg
        const chat = state.chats[chatId]

        if (chat) {
          chat.isLoadingMoreMessages = true
        }
      })
      .addCase(loadMorePlaygroundMessages.fulfilled, (state, action) => {
        const { chatId, messages, hasMore } = action.payload
        const chat = state.chats[chatId]

        if (!chat) return

        // Deduplicate: filter out any messages we already have
        const existingIds = new Set(chat.messages.map((m) => m.id))
        const newMessages = messages.filter((m) => !existingIds.has(m.id))

        // Prepend older messages to the beginning (they're already in chronological order)
        chat.messages = [...newMessages, ...chat.messages]
        chat.hasMoreHistory = hasMore
        chat.isLoadingMoreMessages = false
      })
      .addCase(loadMorePlaygroundMessages.rejected, (state, action) => {
        const { chatId } = action.meta.arg
        const chat = state.chats[chatId]

        if (chat) {
          chat.isLoadingMoreMessages = false
        }
      })
  }
})

export const playgroundChatReducer = playgroundChatSlice.reducer
export const playgroundChatActions = playgroundChatSlice.actions
